import { createAsyncThunk } from '@reduxjs/toolkit';
import type { EnginAffectation } from '@/types';
import api from '@/api/client';
import { parsePaginatedResponse } from '@/api/types';
import { setEnginAffectations, editEnginAffectation, removeEnginAffectation } from '../slices/enginAffectationSlice';
import { setToast } from '../slices/toastSlice';

const PAGE_SIZE = 10;

export const fetchEnginAffectations = createAsyncThunk<void, number | undefined, { rejectValue: string }>(
  'enginAffectation/fetch',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/engins/affectations', {
          params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' },
        });
        const { list, pageNum, totalPages } = parsePaginatedResponse<EnginAffectation>(data);
        dispatch(setEnginAffectations({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: EnginAffectation[] }>('/engins/affectations');
        dispatch(setEnginAffectations(Array.isArray(data?.data) ? data.data : []));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur chargement affectations engins';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const createEnginAffectation = createAsyncThunk<
  boolean,
  Partial<EnginAffectation> & { chantier?: { id: number }; engine?: { id: number }; date_d?: string },
  { rejectValue: string }
>('enginAffectation/create', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await api.post('/engins/affectations', payload);
    await dispatch(fetchEnginAffectations(0));
    dispatch(setToast({ message: 'Affectation engin créée', severity: 'success' }));
    return true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur création';
    dispatch(setToast({ message, severity: 'error' }));
    return rejectWithValue(message);
  }
});

export const updateEnginAffectation = createAsyncThunk<boolean, EnginAffectation, { rejectValue: string }>(
  'enginAffectation/update',
  async (affectation, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/engins/affectations/${affectation.id}`, affectation);
      dispatch(editEnginAffectation(affectation));
      dispatch(setToast({ message: 'Affectation engin modifiée', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const deleteEnginAffectation = createAsyncThunk<
  boolean,
  EnginAffectation,
  { rejectValue: string }
>('enginAffectation/delete', async (affectation, { dispatch, rejectWithValue }) => {
  try {
    const engineId = affectation.engine?.id;
    const chantierId = affectation.chantier?.id;
    if (engineId == null || chantierId == null) {
      dispatch(setToast({ message: 'Données invalides', severity: 'error' }));
      return rejectWithValue('Données invalides');
    }
    await api.delete(`/engins/affectations/${engineId}/${chantierId}`);
    dispatch(removeEnginAffectation({ engine_id: engineId, chantier_id: chantierId }));
    dispatch(setToast({ message: 'Affectation engin supprimée', severity: 'success' }));
    return true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur suppression';
    dispatch(setToast({ message, severity: 'error' }));
    return rejectWithValue(message);
  }
});
